import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  CardHeader,
  Divider,
  Grid,
  Link,
  Typography,
} from '@material-ui/core';
import {
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFileCircleQuestion,
  faFileImage,
} from '@fortawesome/free-solid-svg-icons';
import { makeStyles } from '@material-ui/core/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDateISO } from 'src/utils/date';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
  },
  cardContainer: {
    backgroundColor: theme.palette.background.dark,
  },
  link: {
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.2,
  },
  fontWeightMedium: {
    fontWeight: theme.typography.fontWeightMedium,
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    fontSize: 14,
  },
  cardHeader: {
    color: theme.palette.secondary.main,
  },
  addButton: {
    float: 'right',
    top: -2,
  },
}));

function Attachments({ purchaseOrderId, purchaseOrderAttachments = [],  purchaseOrderService}) {
  const classes = useStyles();
  const [fileList, setFileList] = useState(purchaseOrderAttachments);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    setFileList(purchaseOrderAttachments);
  }, [purchaseOrderAttachments]);

  const handleFileUpload = async (file) => {
    const response = await purchaseOrderService.uploadPurchaseOrderAttachments(
      purchaseOrderId,
      file
    );
    setFileList((prev) => [...prev, response]);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) await handleFileUpload(file);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) await handleFileUpload(file);
  };

  const getIcon = (fileType) => {
    if (fileType?.includes('pdf')) return <FontAwesomeIcon size="2x" icon={faFilePdf} />;
    if (fileType?.includes('officedocument.word')) return <FontAwesomeIcon size="2x" icon={faFileWord} />;
    if (fileType?.includes('officedocument.spread')) return <FontAwesomeIcon size="2x" icon={faFileExcel} />;
    if (fileType?.includes('image')) return <FontAwesomeIcon size="2x" icon={faFileImage} />;
    return <FontAwesomeIcon size="2x" icon={faFileCircleQuestion} />;
  };

  return (
    <Card
      className={classes.card}
      style={{ border: isDragging ? '2px solid green' : 'none' }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragEnter}
      onDrop={handleDrop}
    >
      <CardHeader
        className={classes.cardHeader}
        title={
          <>
            <span style={{ marginTop: 8, display: 'inline-block' }}>Attachments</span>
            <Button
              className={classes.addButton}
              variant="contained"
              color="primary"
              component="label"
            >
              <input type="file" hidden onChange={handleFileChange} />
              Attach Files
            </Button>
          </>
        }
      />
      <Divider />

      <Grid container spacing={3} style={{ padding: 13 }}>
        {fileList.map((file, index) => (
          <Grid
            item
            key={`${file.fileName}-${index}`}
            xs={12}
            sm={6}
            md={6}
            lg={4}
            xl={3}
            style={{ padding: 15 }}
          >
            <Link
              target="_blank"
              href={file.url}
              title={`${formatDateISO(file.uploadedDate)} by ${file.uploadedBy}`}
              className={classes.link}
            >
              {getIcon(file.fileType)}
              <div style={{ display: 'inline-block', paddingLeft: 10 }}>
                <Typography variant="body2" color="textSecondary" className={classes.fontWeightMedium}>
                  {file.fileName}
                </Typography>
                <br />
                <small>
                  {formatDateISO(file.uploadedDate)} by {file.uploadedBy}
                </small>
              </div>
            </Link>
          </Grid>
        ))}
      </Grid>
    </Card>
  );
}

Attachments.propTypes = {
  purchaseOrderId: PropTypes.number.isRequired,
  purchaseOrderAttachments: PropTypes.arrayOf(
    PropTypes.shape({
      fileName: PropTypes.string,
      fileType: PropTypes.string,
      uploadedDate: PropTypes.string,
      uploadedBy: PropTypes.string,
      url: PropTypes.string,
    })
  ),
};

export default Attachments;